import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, Sparkles, Check, Loader2 } from "lucide-react";
import { getBackendUrl } from "../../lib/utils";

const BACKEND = getBackendUrl();
const API = `${BACKEND}/api`;

const EXAMPLES = [
  "I struggled with past subjunctive again",
  "I want to reach B2 by July for a job in Berlin",
  "I can never pronounce the rolling R",
  "I freeze when someone asks me about my family",
];

const AccentDot = ({ accent }) => {
  const style =
    accent === "cyan" ? { background: "rgb(var(--accent-cyan-rgb))", boxShadow: "0 0 12px rgb(var(--accent-cyan-rgb))" }
    : accent === "blue" ? { background: "rgb(var(--accent-blue-rgb))", boxShadow: "0 0 12px rgb(var(--accent-blue-rgb))" }
    : { background: "rgb(var(--accent-violet-rgb))", boxShadow: "0 0 12px rgb(var(--accent-violet-rgb))" };
  return <span className="inline-block w-2 h-2 rounded-full" style={style} />;
};

const MemoryDemo = () => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [nodes, setNodes] = useState([]);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if (!value) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/memory/analyze`, { text: value });
      setNodes((prev) => [res.data, ...prev].slice(0, 6));
      setText("");
    } catch (err) {
      setError("Could not save memory. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-28 px-6" data-testid="section-memory-demo">
      <div className="max-w-7xl mx-auto">
        <div className="section-label">Live memory demo</div>
        <div className="mt-4 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 className="headline text-4xl sm:text-5xl lg:text-[72px] max-w-3xl">
            Drop a thought. <em>Watch</em> her remember.
          </h2>
          <p className="text-whisper text-[16px] leading-[1.55] max-w-md">
            Type a mistake, hesitation, or goal. AURA turns it into a memory node.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-4">
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 glass rounded-3xl p-6 edge-glow relative overflow-hidden"
            data-testid="memory-demo-form"
          >
            <div className="absolute -top-12 -right-12 w-[300px] h-[300px] rounded-full bg-aura-cyan/15 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                <Sparkles size={12} className="text-aura-cyan" /> new memory
              </div>
              <textarea
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. I keep confusing ser and estar when I'm tired"
                className="mt-4 w-full bg-panel/[0.02] border border-panel/10 rounded-2xl p-4 text-[15px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/60 focus:ring-[3px] focus:ring-aura-cyan/10 resize-none"
                data-testid="memory-demo-input"
                onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") submit(); }}
              />
              <div className="mt-2 font-mono text-[10.5px] tracking-[0.18em] uppercase text-whisper-muted flex items-center gap-2">
                <span className="kbd">⌘</span><span className="kbd">Enter</span><span>to remember</span>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {EXAMPLES.map((ex) => (
                  <button
                    type="button"
                    key={ex}
                    onClick={() => setText(ex)}
                    className="text-[12px] font-mono tracking-[0.08em] px-3 py-1.5 rounded-full border border-panel/10 text-whisper hover:border-aura-cyan/60 hover:text-ink transition-colors"
                    data-testid="memory-demo-example"
                  >
                    {ex}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button type="submit" disabled={loading || !text.trim()} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed" data-testid="memory-demo-submit">
                  {loading ? (<><Loader2 size={14} className="animate-spin" /> remembering…</>) : (<>save memory <ArrowRight size={14} /></>)}
                </button>
                {error && <span className="font-mono text-[11px] text-red-300">{error}</span>}
              </div>
            </div>
          </motion.form>

          <div className="lg:col-span-7">
            <div className="flex items-center justify-between font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
              <span>Your memory feed</span>
              <span>{nodes.length} node{nodes.length === 1 ? "" : "s"} this session</span>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <AnimatePresence initial={false}>
                {nodes.map((n, idx) => (
                  <motion.div
                    key={n.id}
                    initial={{ opacity: 0, y: 14, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                    className="node-card p-5"
                    data-testid={`memory-node-${idx}`}
                  >
                    <div className="relative z-10 flex items-center justify-between">
                      <div className="flex items-center gap-2 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">
                        <AccentDot accent={n.accent} />
                        {n.category}
                      </div>
                      <div className="text-[10.5px] font-mono tracking-[0.18em] uppercase text-whisper-muted">{n.cefr_hint}</div>
                    </div>
                    <h4 className="relative z-10 mt-3 font-display text-[19px] font-medium tracking-tight text-ink">{n.label}</h4>
                    <p className="relative z-10 mt-2 text-whisper text-[14px] leading-[1.55]">{n.detail}</p>
                    <div className="relative z-10 mt-4 pt-4 border-t border-panel/5 text-[13px] text-whisper italic">
                      <span className="font-mono not-italic text-[10px] tracking-[0.22em] uppercase text-aura-cyan mr-2">next</span>
                      {n.recommendation}
                    </div>
                    <div className="relative z-10 mt-4 flex flex-wrap gap-1.5">
                      {n.tags.map((t) => (
                        <span key={t} className="text-[10.5px] font-mono tracking-[0.08em] px-2 py-1 rounded-full border border-panel/10 text-whisper-muted">#{t}</span>
                      ))}
                    </div>
                  </motion.div>
                ))}
                {nodes.length === 0 && (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="sm:col-span-2 hairline rounded-3xl p-10 text-center text-whisper" data-testid="memory-demo-empty">
                    <Check size={14} className="mx-auto text-aura-cyan" />
                    <div className="mt-3 font-mono text-[10.5px] tracking-[0.22em] uppercase text-whisper-muted">Waiting for your first memory</div>
                    <div className="mt-2 font-serif italic text-[18px] text-ink/80">Give her something worth remembering.</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MemoryDemo;
